import React, { useState } from 'react';

const POLICY = {
  none: {
    label: 'Monitor Only',
    step: 1,
    color: '#F5A623',
    icon: '◉',
    plain: 'Receiving servers accept all email — nothing is blocked or filtered. You\'re collecting visibility data only.',
    consequence: 'Phishing emails impersonating your domain are delivered to recipients.',
    next: 'Once your pass rate holds above 95% for 30 days, advance to Quarantine.',
  },
  quarantine: {
    label: 'Quarantine',
    step: 2,
    color: '#F5A623',
    icon: '◈',
    plain: 'Failing emails are routed to the recipient\'s spam/junk folder instead of the inbox.',
    consequence: 'Suspicious mail lands in junk — recipients may still see it.',
    next: 'Once pass rate holds above 95% for 30 days at quarantine, advance to Reject.',
  },
  reject: {
    label: 'Full Enforcement',
    step: 3,
    color: '#00C2A8',
    icon: '⬡',
    plain: 'Receiving servers reject failing emails outright. They never reach the inbox or spam folder.',
    consequence: 'Phishing emails impersonating your domain are stopped at the server.',
    next: null,
  },
};

const STEPS = [
  { key: 'none', label: 'Monitor', sub: 'p=none' },
  { key: 'quarantine', label: 'Quarantine', sub: 'p=quarantine' },
  { key: 'reject', label: 'Reject', sub: 'p=reject' },
];

const ALIGN = {
  r: { label: 'Relaxed', desc: 'Subdomain match is acceptable' },
  s: { label: 'Strict', desc: 'Exact domain match required' },
};

function MaturityBar({ currentPolicy }) {
  const currentStep = POLICY[currentPolicy]?.step || 1;
  return (
    <div style={{ display: 'flex', gap: 0, marginBottom: '20px', borderRadius: '8px', overflow: 'hidden' }}>
      {STEPS.map((step, i) => {
        const pInfo = POLICY[step.key];
        const active = step.key === currentPolicy;
        const done = (POLICY[currentPolicy]?.step || 1) > pInfo.step;
        const bg = active ? `${pInfo.color}22` : done ? 'rgba(0,194,168,0.08)' : 'rgba(30,45,79,0.5)';
        const textColor = active ? pInfo.color : done ? '#00C2A8' : '#3A4F6E';
        const borderColor = active ? `${pInfo.color}50` : done ? 'rgba(0,194,168,0.2)' : 'rgba(30,45,79,0.8)';
        return (
          <div key={step.key} style={{
            flex: 1,
            padding: '8px 12px',
            background: bg,
            border: `1px solid ${borderColor}`,
            borderLeft: i === 0 ? `1px solid ${borderColor}` : 'none',
            textAlign: 'center',
          }}>
            <div style={{ fontFamily: 'Space Grotesk', fontSize: '12px', fontWeight: active ? 700 : 500, color: textColor }}>
              {active ? '▶ ' : done ? '✓ ' : ''}{step.label}
            </div>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: active ? textColor : '#2A3B5A', marginTop: '2px' }}>
              {step.sub}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PolicyCallout({ policy, next }) {
  const info = POLICY[policy] || POLICY.none;
  return (
    <div style={{ marginTop: '16px', padding: '12px 14px', borderRadius: '8px', background: 'rgba(15,22,41,0.5)', border: `1px solid ${info.color}20` }}>
      <div style={{ fontFamily: 'Inter', fontSize: '12px', color: '#7B9CC8', lineHeight: 1.6 }}>
        <span style={{ color: info.color, fontWeight: 600 }}>{info.label}: </span>
        {info.plain}
      </div>
      <div style={{ marginTop: '8px', fontFamily: 'Inter', fontSize: '12px', color: '#5B7BA8', lineHeight: 1.5, paddingLeft: '0px', borderTop: '1px solid rgba(91,123,168,0.15)', paddingTop: '8px' }}>
        <span style={{ color: policy === 'reject' ? '#00C2A8' : '#E8415A', marginRight: '4px' }}>
          {policy === 'reject' ? '✓' : '⚠'}
        </span>
        {info.consequence}
      </div>
      {info.next && (
        <div style={{ marginTop: '6px', fontFamily: 'Inter', fontSize: '11px', color: '#3A5A8A', fontStyle: 'italic' }}>
          Next: {info.next}
        </div>
      )}
    </div>
  );
}

function AlignmentPills({ adkim, aspf }) {
  const pills = [
    { label: `DKIM: ${ALIGN[adkim]?.label || adkim}`, title: `adkim=${adkim} — ${ALIGN[adkim]?.desc}`, color: '#5B7BA8' },
    { label: `SPF: ${ALIGN[aspf]?.label || aspf}`, title: `aspf=${aspf} — ${ALIGN[aspf]?.desc}`, color: '#5B7BA8' },
  ];
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px' }}>
      {pills.map(p => (
        <span key={p.label} title={p.title} style={{
          fontFamily: 'JetBrains Mono', fontSize: '10px', color: p.color,
          background: 'rgba(30,45,79,0.7)', padding: '3px 8px', borderRadius: '4px',
          border: '1px solid rgba(91,123,168,0.2)', cursor: 'help',
        }}>
          {p.label}
        </span>
      ))}
    </div>
  );
}

export default function DomainCards({ domains }) {
  const [expanded, setExpanded] = useState({});

  if (!domains?.length) return (
    <div style={{ textAlign: 'center', padding: '60px', color: '#5B7BA8', fontFamily: 'Inter' }}>
      No domain data available
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '20px' }}>
      {domains.map(domain => {
        const rate = domain.total > 0 ? Math.round((domain.pass / domain.total) * 100) : 0;
        const rateColor = rate >= 90 ? '#00C2A8' : rate >= 70 ? '#F5A623' : '#E8415A';
        const p = domain.policy?.p || 'none';
        const pInfo = POLICY[p] || POLICY.none;
        const isOpen = expanded[domain.domain];

        return (
          <div key={domain.domain} style={{
            background: 'rgba(30,45,79,0.5)',
            borderRadius: '16px',
            border: `1px solid ${rateColor}25`,
            padding: '24px',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Glow */}
            <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '120px', height: '120px', borderRadius: '50%', background: `radial-gradient(circle, ${rateColor}12, transparent 70%)`, pointerEvents: 'none' }} />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: '16px', fontWeight: 500, color: '#F0F4FF' }}>
                {domain.domain}
              </div>
              <div style={{ fontFamily: 'Space Grotesk', fontSize: '32px', fontWeight: 700, color: rateColor, lineHeight: 1 }}>
                {rate}%
              </div>
            </div>

            {/* Maturity stepper */}
            <MaturityBar currentPolicy={p} />

            {/* Pass bar */}
            <div style={{ height: '8px', background: '#1E2D4F', borderRadius: '4px', overflow: 'hidden', marginBottom: '16px' }}>
              <div style={{ height: '100%', width: `${rate}%`, background: `linear-gradient(90deg, ${rateColor}, ${rateColor}99)`, borderRadius: '4px', boxShadow: `0 0 8px ${rateColor}60`, transition: 'width 1s ease' }} />
            </div>

            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Total', value: domain.total.toLocaleString(), color: '#7B9CC8' },
                { label: 'Pass', value: domain.pass.toLocaleString(), color: '#00C2A8' },
                { label: 'Fail', value: domain.fail.toLocaleString(), color: domain.fail > 0 ? '#E8415A' : '#5B7BA8' },
              ].map(stat => (
                <div key={stat.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'JetBrains Mono', fontSize: '16px', fontWeight: 600, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontFamily: 'Inter', fontSize: '11px', color: '#5B7BA8', marginTop: '2px' }}>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Alignment pills */}
            <AlignmentPills adkim={domain.policy?.adkim || 'r'} aspf={domain.policy?.aspf || 'r'} />

            {/* pct warning */}
            {domain.policy?.pct != null && domain.policy.pct < 100 && (
              <div style={{ marginTop: '12px', padding: '8px 12px', background: 'rgba(245,166,35,0.1)', borderRadius: '6px', border: '1px solid rgba(245,166,35,0.2)' }}>
                <span style={{ fontFamily: 'Inter', fontSize: '12px', color: '#F5A623' }}>
                  ⚠ Policy applies to {domain.policy.pct}% of messages only
                </span>
              </div>
            )}

            {/* Policy explanation toggle */}
            <button
              onClick={() => setExpanded(s => ({ ...s, [domain.domain]: !s[domain.domain] }))}
              style={{
                marginTop: '14px', width: '100%', padding: '8px', borderRadius: '6px',
                background: 'transparent', border: '1px solid rgba(91,123,168,0.2)',
                color: '#5B7BA8', fontFamily: 'Inter', fontSize: '12px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}
            >
              <span style={{ color: pInfo.color }}>{pInfo.icon}</span>
              {isOpen ? 'Hide policy details' : `What does ${pInfo.label} mean?`}
              <span style={{ fontSize: '10px' }}>{isOpen ? '▲' : '▼'}</span>
            </button>

            {isOpen && <PolicyCallout policy={p} />}
          </div>
        );
      })}
    </div>
  );
}
